<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MCompanyEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: false,
    },
    target: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getPayload = (...args) => {
    const [editor] = args;
    console.log(editor);
    return {
      id: editor.id,
      etag: editor.getETAG(),
      ...editor.getChanges({ skipEmbeddedLists: true }),
    };
  };

  const formId = ref(null);
  const defaultTitle = ref(
    $fn.value.pgettext('Company', 'Edit General Information')
  );
  const targetConfig = ref({
    general: {
      title: $fn.value.pgettext('Company', 'Edit general data'),
      fieldsToSubmit: ['*'],
    },
  });
  const editor = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    defaultTitle: defaultTitle.value,
    targetConfig: targetConfig.value,
    editor: editor.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_editor = async () => {
    return props.item.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'defaultTitle' },
      { name: 'targetConfig' },
      { name: 'editor' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId, editor: $$load_editor },
    ux: {},
    dataRefs: {
      formId,
      defaultTitle,
      targetConfig,
      editor,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: props.target
          ? targetConfig.value[props.target].title
          : defaultTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.target,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('target');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_q5uwzddk0_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_4x71edlrj_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_9dw88mc4c_getActionArgs = async ($event) => {
    return {
      objectType: 'company',
      op: 'upsert',
      data: getPayload(editor.value),
    };
  };
  const act_2434514588044777_closeModal_5jvd20g0s_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_q5uwzddk0 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_q5uwzddk0_getActionArgs,
    });
  const inst_evh_7315092382398562_close_hzt303joe =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_q5uwzddk0,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_hzt303joe],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_kxjycqyav =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_do6d5o74a =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_kxjycqyav,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_do6d5o74a],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_px0vgkgv5 =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_0t2ka5gw9 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_px0vgkgv5,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_0t2ka5gw9],
    event: 'close',
  });
  const act_1040167445267876_closeModal_4x71edlrj =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_4x71edlrj_getActionArgs,
    });
  const inst_evh_1040167445267876_success_kgv8t7iig =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_4x71edlrj,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_9dw88mc4c = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_9dw88mc4c_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_kgv8t7iig],
  });
  const inst_evh_1040167445267876_submit_db1ht0ten =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_9dw88mc4c,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_db1ht0ten],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_5jvd20g0s =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_5jvd20g0s_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_h7llfz1xr =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_5jvd20g0s,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_h7llfz1xr],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_hzt303joe,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_do6d5o74a,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_0t2ka5gw9,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_kgv8t7iig,
    inst_evh_1040167445267876_submit_db1ht0ten,
    save: inst_evh_1040167445267876_submit_db1ht0ten,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_h7llfz1xr,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_hzt303joe = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_hzt303joe.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_do6d5o74a = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_do6d5o74a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_0t2ka5gw9 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_0t2ka5gw9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_kgv8t7iig = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_kgv8t7iig.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_db1ht0ten = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_db1ht0ten.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_h7llfz1xr = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_h7llfz1xr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MCompanyEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="company"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <MCompanyEditorFieldset
            :item="editor"
            :formId="formId"
            :target="target"
          >
          </MCompanyEditorFieldset>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.company || !forms.company.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
